// src/pages/CheckoutPage.js
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const GEOAPIFY_KEY = process.env.REACT_APP_GEOAPIFY_KEY;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function CheckoutPage() {
  const { cart } = useCart();

  const [customer, setCustomer] = useState({
    name: '', phone: '', email: '',
    addressLine1: '', addressLine2: '',
    city: '', province: '', postalCode: '',
    coord: null
  });

  const [loading, setLoading] = useState(false);

  // AUTOCOMPLETE STATE
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [hasSelectedAddress, setHasSelectedAddress] = useState(false);

  const [estimate, setEstimate] = useState(null);
  const [deliverable, setDeliverable] = useState(true);
  const [deliveryMessage, setDeliveryMessage] = useState('');

  const debounceRef = useRef(null);
  const autocompleteRef = useRef(null);

  const sessionID = localStorage.getItem("sessionID");

  const total = cart.reduce((s, i) => s + i.subtotal, 0);
  const provinces = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];

  /* ================= AUTOCOMPLETE ================= */
  useEffect(() => {
    if (hasSelectedAddress) return;

    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
          query
        )}&limit=6&filter=countrycode:ca&lang=en&apiKey=${GEOAPIFY_KEY}`;

        const res = await fetch(url);
        const data = await res.json();
        setSuggestions(data.features || []);
      } catch (err) {
        console.error('autocomplete error', err);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query, hasSelectedAddress]);

  /* ================= OUTSIDE CLICK ================= */
  useEffect(() => {
    const handleClickOutside = e => {
      if (!autocompleteRef.current?.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setCustomer(c => ({ ...c, [name]: value }));
  };

  const handlePickSuggestion = feat => {
    const prop = feat.properties || {};
    const geom = feat.geometry || {};
    const coord = { lat: geom.coordinates[1], lon: geom.coordinates[0] };

    setCustomer(c => ({
      ...c,
      addressLine1: prop.address_line || prop.formatted || '',
      city: prop.city || prop.county || '',
      province: prop.state || '',
      postalCode: prop.postcode || '',
      coord
    }));

    // 🔑 HARD STOP autocomplete
    setQuery('');
    setSuggestions([]);
    setHasSelectedAddress(true);

    fetchEstimate({ coord });
  };

  // const buildItems = () =>
  //   cart.map(i => ({
  //     productId: i.productId._id,
  //     name: i.name,
  //     amount: Math.round(i.productId.price * 100),
  //     quantity: i.quantity,
  //     taxable: i.productId.taxable
  //   }));

  const fetchEstimate = async ({ coord = customer.coord } = {}) => {
    if (!coord) return;
    try {
      const res = await fetch(`${API_BASE_URL}/checkout/estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destination: coord, sessionID })
      });
      const json = await res.json();

      if (!res.ok) {
        setEstimate(null);
        setDeliverable(false);
        setDeliveryMessage(json.error || 'Failed to get estimate');
        return;
      }

      setEstimate(json);
      setDeliverable(Boolean(json.deliverable));
      setDeliveryMessage(json.message);
    } catch {
      setEstimate(null);
      setDeliverable(false);
      setDeliveryMessage('Estimate failed.');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!deliverable) {
      alert(deliveryMessage || "We don't deliver there yet.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
  customer,
  sessionID
})
      });

      const json = await res.json();
      setLoading(false);

      if (!res.ok) {
        alert(json.message || json.error || 'Error initiating payment');
        return;
      }

      window.location.href = json.url;
    } catch {
      setLoading(false);
      alert('Error initiating payment');
    }
  };

  return (
    <div className="max-w-6xl mx-auto mt-24 px-4 py-8 grid md:grid-cols-2 gap-10 font-[Poppins] bg-[#fcfdfc]">
      
      {/* LEFT - Customer Details Form */}
      <form onSubmit={handleSubmit} className="space-y-6 bg-white border border-slate-100 rounded-3xl shadow-sm p-6 sm:p-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-800 font-[Montserrat] mb-1.5 flex items-center gap-2">
            <i className="bx bx-user-pin text-green-700 text-2xl" /> Checkout Details
          </h2>
          <p className="text-xs text-slate-400 font-medium">Please enter your delivery info and address</p>
        </div>

        <div className="space-y-4">
          {/* Name input */}
          <div className="relative">
            <i className="bx bx-user absolute left-4 top-3.5 text-slate-400 text-lg" />
            <input
              name="name"
              type="text"
              placeholder="Full Name"
              value={customer.name}
              onChange={handleChange}
              required
              className="w-full bg-slate-50 border border-slate-200 focus:border-green-600 rounded-2xl pl-11 pr-4 py-3 text-sm outline-none focus:ring-4 focus:ring-green-700/5 transition-all font-medium"
            />
          </div>

          {/* Phone input */}
          <div className="relative">
            <i className="bx bx-phone absolute left-4 top-3.5 text-slate-400 text-lg" />
            <input
              name="phone"
              type="tel"
              placeholder="Phone Number"
              value={customer.phone}
              onChange={handleChange}
              required
              className="w-full bg-slate-50 border border-slate-200 focus:border-green-600 rounded-2xl pl-11 pr-4 py-3 text-sm outline-none focus:ring-4 focus:ring-green-700/5 transition-all font-medium"
            />
          </div>

          {/* Email input */}
          <div className="relative">
            <i className="bx bx-envelope absolute left-4 top-3.5 text-slate-400 text-lg" />
            <input
              name="email"
              type="email"
              placeholder="Email Address"
              value={customer.email}
              onChange={handleChange}
              required
              className="w-full bg-slate-50 border border-slate-200 focus:border-green-600 rounded-2xl pl-11 pr-4 py-3 text-sm outline-none focus:ring-4 focus:ring-green-700/5 transition-all font-medium"
            />
          </div>

          {/* ADDRESS INPUT WITH AUTOCOMPLETE */}
          <div className="relative" ref={autocompleteRef}>
            <i className="bx bx-map-pin absolute left-4 top-3.5 text-slate-400 text-lg" />
            <input
              placeholder="Delivery Address (Line 1)"
              value={hasSelectedAddress ? customer.addressLine1 : query}
              onChange={e => {
                setQuery(e.target.value);
                setHasSelectedAddress(false);
                setDeliverable(true);
                setEstimate(null);
              }}
              autoComplete="off"
              required
              className="w-full bg-slate-50 border border-slate-200 focus:border-green-600 rounded-2xl pl-11 pr-4 py-3 text-sm outline-none focus:ring-4 focus:ring-green-700/5 transition-all font-medium"
            />

            {suggestions.length > 0 && (
              <ul className="absolute z-10 w-full bg-white border border-slate-100 rounded-2xl mt-1.5 shadow-xl max-h-48 overflow-y-auto p-1.5 space-y-0.5">
                {suggestions.map(feat => (
                  <li
                    key={feat.properties.place_id}
                    className="p-2.5 rounded-xl hover:bg-green-50 hover:text-green-800 text-xs sm:text-sm font-semibold text-slate-600 cursor-pointer transition-colors"
                    onMouseDown={() => handlePickSuggestion(feat)}
                  >
                    {feat.properties.formatted}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !deliverable}
          className="w-full bg-gradient-to-r from-green-700 to-green-600 hover:from-green-800 hover:to-green-700 text-white py-3.5 rounded-2xl font-bold transition-all shadow-[0_8px_20px_-4px_rgba(21,128,61,0.2)] disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-2 mt-4"
        >
          {loading ? (
            <>
              <i className="bx bx-loader-alt animate-spin text-lg" />
              <span>Processing Payment...</span>
            </>
          ) : (
            <>
              <i className="bx bx-credit-card text-lg" />
              <span>Proceed to Payment</span>
            </>
          )}
        </button>

        {!deliverable && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 bg-red-50 border border-red-100 text-red-700 rounded-xl text-xs font-semibold flex items-center gap-2 mt-4"
          >
            <i className="bx bx-error-circle text-lg" />
            <span>{deliveryMessage}</span>
          </motion.div>
        )}
      </form>

      {/* RIGHT - Order Summary */}
      <div className="bg-white border border-slate-100 shadow-sm rounded-3xl p-6 sm:p-8 h-fit sticky top-24 space-y-6">
        <div>
          <h2 className="text-lg sm:text-xl font-black text-slate-800 font-[Montserrat] mb-1.5">
            Order Summary
          </h2>
          <p className="text-xs text-slate-400 font-medium">Verify items in your purchase</p>
        </div>

        <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto pr-1">
          {cart.map(item => (
            <li key={item.productId._id} className="py-3 flex justify-between items-center text-xs sm:text-sm font-semibold text-slate-700">
              <span className="line-clamp-1 max-w-[280px]">{item.name} <span className="text-slate-400 text-xs">× {item.quantity}</span></span>
              <span className="font-bold text-slate-800">${item.subtotal.toFixed(2)}</span>
            </li>
          ))}
        </ul>

        <div className="bg-slate-50 p-4.5 rounded-2xl space-y-2.5 text-xs sm:text-sm font-semibold text-slate-600">
          <div className="flex justify-between">
            <span>Items total</span>
            <span className="text-slate-800 font-bold">${total.toFixed(2)}</span>
          </div>

          {estimate ? (
            <div className="space-y-2.5 border-t border-slate-200/50 pt-2.5">
              <div className="flex justify-between">
                <span>Shipping</span>
                <span className="text-slate-800 font-bold">${(estimate.shipping_total/100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>HST Tax</span>
                <span className="text-slate-800 font-bold">${(estimate.tax/100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm sm:text-base font-black text-slate-800 pt-1.5 border-t border-slate-200/50">
                <span>Total</span>
                <span className="text-green-700 font-black">${(estimate.grand_total/100).toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <div className="border-t border-slate-200/50 pt-2.5 flex items-center gap-1.5 text-[11px] text-slate-400">
              <i className="bx bx-info-circle text-sm text-slate-400" />
              <span>Enter address to view shipping estimate & tax</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
